import { INNER_BORDER_COLOR, OUTER_BORDER_COLOR } from "../../styles/colors";
import {
  TOTAL_NUMBER_FORMAT,
  allEdges,
  applyGridStyle,
  applyRangeFormats,
  rangeFormatting,
} from "../../styles/worksheet";
import { WORKSHEET_CUSTOM_PROPERTIES, getTemplateByReportTypeOrNull } from "../../workbook/customProperties";
import { templateSheetName } from "../../workbook/tableNames";
import { UNIT_MIX_REPORT_NAME } from "../api/reportNames";
import { columnNames, rowNames } from "../helpers/rangeNames";
import { generateUnitMixReports } from "../unitMix";
import { titleMaker } from "./common";

const buildUnitMixTemplate = async (context: Excel.RequestContext, reportTitle: string) => {
  const workbook = context.workbook;
  const templateWorksheet = workbook.worksheets.add(templateSheetName(reportTitle));
  await context.sync();

  await titleMaker(templateWorksheet, reportTitle);

  const rowHeights = [12, 30, 25, 25, 12, 25];
  rowHeights.forEach((height, index) => {
    templateWorksheet.getRange(`${index + 5}:${index + 5}`).format.rowHeight = height;
  });

  const columnWidths = [20, 120, 80, 12, 100, 12, 100, 100, 100];
  columnWidths.forEach((width, index) => {
    templateWorksheet.getRange(
      `${String.fromCharCode(65 + index)}:${String.fromCharCode(65 + index)}`
    ).format.columnWidth = width;
  });

  templateWorksheet.getRange("1:10").format.verticalAlignment = Excel.VerticalAlignment.center;
  await context.sync();

  const headers: rangeFormatting[] = [
    { rangeAddress: "B6", value: "Floor", name: "\\floor_name", Halignment: "Center" },
    { rangeAddress: "C6", value: "Totals", name: "\\totals", Halignment: "Center" },
    { rangeAddress: "E6", name: "\\unit_types", Halignment: "Center" },
    { rangeAddress: "G6", value: "Net Area (SF)", name: "\\net_area", Halignment: "Center" },
    { rangeAddress: "H6", value: "Gross Area (SF)", name: "\\gross_area", Halignment: "Center" },
    { rangeAddress: "I6", value: "Efficiency (%)", name: "\\efficiency", Halignment: "Center" },
  ];
  headers.forEach((header) => {
    header.fontSize = 12;
    header.bold = true;
  });
  applyRangeFormats(templateWorksheet, headers);

  const dataRows: rangeFormatting[] = [
    { rangeAddress: "B7:I8", name: rowNames.unitMixItems },
    { rangeAddress: "C7", numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
    { rangeAddress: "E7", numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
    { rangeAddress: "G7", numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
    { rangeAddress: "H7", numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
    { rangeAddress: "I7", numberFormat: "0%;(0)%;-", Halignment: "Right" },
  ];
  applyRangeFormats(templateWorksheet, dataRows);

  // Grid styling
  // First group
  applyGridStyle({
    range: templateWorksheet.getRange("B7:C8"),
    borderColor: INNER_BORDER_COLOR,
    weight: "Thin",
    edges: allEdges,
  });
  applyGridStyle({
    range: templateWorksheet.getRange("A7"),
    borderColor: OUTER_BORDER_COLOR,
    weight: "Medium",
    edges: ["EdgeRight"],
  });
  applyGridStyle({
    range: templateWorksheet.getRange("D7"),
    borderColor: OUTER_BORDER_COLOR,
    weight: "Medium",
    edges: ["EdgeLeft"],
  });
  // spacer row
  applyGridStyle({
    range: templateWorksheet.getRange("B9:C9"),
    borderColor: OUTER_BORDER_COLOR,
    weight: "Medium",
    edges: ["EdgeTop"],
  });

  // Second group
  applyGridStyle({
    range: templateWorksheet.getRange("E7:E8"),
    borderColor: INNER_BORDER_COLOR,
    weight: "Thin",
    edges: allEdges,
  });
  applyGridStyle({
    range: templateWorksheet.getRange("D7"),
    borderColor: OUTER_BORDER_COLOR,
    weight: "Medium",
    edges: ["EdgeRight"],
  });
  applyGridStyle({
    range: templateWorksheet.getRange("F7"),
    borderColor: OUTER_BORDER_COLOR,
    weight: "Medium",
    edges: ["EdgeLeft"],
  });
  // spacer row
  applyGridStyle({
    range: templateWorksheet.getRange("E9"),
    borderColor: OUTER_BORDER_COLOR,
    weight: "Medium",
    edges: ["EdgeTop"],
  });

  // Third group (bottom and right border gets applied dynamically in report generation)
  applyGridStyle({
    range: templateWorksheet.getRange("G7:I8"),
    borderColor: INNER_BORDER_COLOR,
    weight: "Thin",
    edges: allEdges,
  });
  applyGridStyle({
    range: templateWorksheet.getRange("F7"),
    borderColor: OUTER_BORDER_COLOR,
    weight: "Medium",
    edges: ["EdgeRight"],
  });

  // first group header
  applyGridStyle({
    range: templateWorksheet.getRange("B6:C6"),
    borderColor: OUTER_BORDER_COLOR,
    weight: "Medium",
    edges: allEdges,
  });

  // second group header
  const unitTypesHeaderRange = templateWorksheet.getRange(`\\${columnNames.unitTypes}`);
  applyGridStyle({
    range: unitTypesHeaderRange,
    borderColor: OUTER_BORDER_COLOR,
    weight: "Medium",
    edges: allEdges,
  });

  // third group header
  applyGridStyle({
    range: templateWorksheet.getRange(`G6:I6`),
    borderColor: OUTER_BORDER_COLOR,
    weight: "Medium",
    edges: allEdges,
  });

  templateWorksheet.customProperties.add(WORKSHEET_CUSTOM_PROPERTIES.REPORT_TYPE, reportTitle);
  templateWorksheet.customProperties.add(WORKSHEET_CUSTOM_PROPERTIES.IS_TEMPLATE, "true");
  await context.sync();

  templateWorksheet.showGridlines = false;
  templateWorksheet.visibility = Excel.SheetVisibility.hidden;
  await context.sync();

  return templateWorksheet;
};

export const generateUnitMixReportsFromTemplate = async () => {
  return Excel.run(async (context) => {
    const reportTitle = UNIT_MIX_REPORT_NAME;
    const template =
      (await getTemplateByReportTypeOrNull(context, reportTitle)) || (await buildUnitMixTemplate(context, reportTitle));
    await generateUnitMixReports(context, template);
  });
};
